import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "../UI/Layout/AdminLayout";
import axios from "axios";
import DataTable from "react-data-table-component";
import Spining from "../UI/Spining/Spining";
import { NODE_HOST_URL_API3_v1 } from '../Dashboard/api/Api';
import styles from "./WhatsappWidgetStatus.module.css";
import { customTableStylesForSummery } from "../Utils/customTableStyles";

const COLUMNS = [
    {
        name: "ID",
        selector: (row) => row.id,
        sortField: "id",
        width: "80px",
        sortable: true,
    },
    {
        name: "Username",
        selector: (row) => row.username,
        sortField: "username",
        minWidth: "200px",
        sortable: true,
    },
    {
        name: "Domain",
        minWidth: "220px",
        cell: (row) => {
            const domains = (row.installed_domains || []).map((d) => d.domain).filter(Boolean);
            if (domains.length === 0) return <span>-</span>;
            return (
                <div style={{ textAlign: "left", padding: "4px 0" }}>
                    {domains.map((domain, i) => {
                        const url = /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
                        return (
                            <span key={i}>
                                <a href={url} target="_blank" rel="noopener noreferrer" className={styles.domain}>
                                    {domain}
                                </a>
                                {i < domains.length - 1 && ", "}
                            </span>
                        );
                    })}
                </div>
            );
        },
    },
    {
        name: "Installed At",
        selector: (row) => row.installed_domains?.[0]?.installed_at || "-",
        sortField: "installed_at",
        width: "190px",
        sortable: true,
    },
    {
        name: "Status",
        width: "130px",
        sortable: true,
        sortField: "status",
        cell: (row) =>
            (row.status === true || row.status === 1) ? (
                <span className={styles.active}>Active</span>
            ) : (
                <span className={styles.inactive}>Inactive</span>
            ),
    },
];

const LiveWidgetTable = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("active");
    const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0 });
    const [dateFilter, setDateFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const fetchData = useCallback(async (page, sort_by = "", sort_order = "asc", searchVal = "", status = "", df = "", fd = "", td = "") => {
        setLoading(true);
        try {
            const loginData = JSON.parse(localStorage.getItem("admin"));
            const user_id = loginData?.user?.userId;
            const token = loginData?.token;

            const body = {
                user_id,
                token,
                method: "retrieve_wp_all_widget",
                page,
                limit: 10,
                sort_by,
                sort_order,
                search: searchVal,
            };
            if (status) body.status = status;
            if (df) {
                body.date_filter = df;
                if (df === "custom" && fd && td) {
                    body.from_date = fd;
                    body.to_date = td;
                }
            }

            const res = await axios.post(`${NODE_HOST_URL_API3_v1}/whatsapp_widget`, body);

            if (res.data.success) {
                setData(res.data.data || []);
                setTotalRows(res.data.total || 0);
                if (res.data.summary) setSummary(res.data.summary);
            }
        } catch (err) {
            console.error("Error fetching widget data:", err);
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    }, []);

    useEffect(() => {
        if (dateFilter === "custom" && (!fromDate || !toDate)) return;
        fetchData(currentPage, sortBy, sortOrder, search, statusFilter, dateFilter, fromDate, toDate);
    }, [currentPage, sortBy, sortOrder, search, statusFilter, dateFilter, fromDate, toDate, fetchData]);

    const handleSort = (column, direction) => {
        setSortBy(column.sortField || "");
        setSortOrder(direction);
        setCurrentPage(1);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setSearch(searchInput);
        setCurrentPage(1);
    };

    const handleSearchClear = () => {
        setSearchInput("");
        setSearch("");
        setCurrentPage(1);
    };

    const handleStatusFilter = (f) => {
        setStatusFilter(f);
        setCurrentPage(1);
    };

    const handleDateFilterChange = (e) => {
        const val = e.target.value;
        setDateFilter(val);
        setCurrentPage(1);
        if (val !== "custom") {
            setFromDate("");
            setToDate("");
        }
    };

    const handleFromDateChange = (e) => {
        setFromDate(e.target.value);
        setCurrentPage(1);
    };

    const handleToDateChange = (e) => {
        setToDate(e.target.value);
        setCurrentPage(1);
    };

    const summaryCards = [
        { label: "Blue",  displayLabel: "Total",    value: summary.total },
        { label: "Green", displayLabel: "Active",   value: summary.active },
        { label: "Red",   displayLabel: "Inactive", value: summary.inactive },
    ];

    return (
        <AdminLayout>
            <div className={styles.container}>
                <div className={styles.topBar}>
                    <h3 className={styles.heading}>Live Widget Status</h3>
                    <form onSubmit={handleSearchSubmit} className={styles.searchForm}>
                        <input
                            type="text"
                            placeholder="Search by username, domain..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className={styles.searchInput}
                        />
                        {search && (
                            <button type="button" onClick={handleSearchClear} className={styles.clearBtn}>
                                &times;
                            </button>
                        )}
                        <button type="submit" className={styles.searchBtn}>Search</button>
                    </form>
                </div>

                {/* Date Filter */}
                <div className={styles.dateFilterRow}>
                    <span className={styles.dateFilterLabel}>Date Filter:</span>
                    <select
                        value={dateFilter}
                        onChange={handleDateFilterChange}
                        className={styles.dateFilterSelect}
                    >
                        <option value="">All Time</option>
                        <option value="today">Today</option>
                        <option value="yesterday">Yesterday</option>
                        <option value="last_7_days">Last 7 Days</option>
                        <option value="last_30_days">Last 30 Days</option>
                        <option value="this_month">This Month</option>
                        <option value="last_month">Last Month</option>
                        <option value="custom">Custom Range</option>
                    </select>
                    {dateFilter === "custom" && (
                        <>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={handleFromDateChange}
                                className={styles.dateInput}
                                max={toDate || undefined}
                            />
                            <span className={styles.dateRangeSep}>to</span>
                            <input
                                type="date"
                                value={toDate}
                                onChange={handleToDateChange}
                                className={styles.dateInput}
                                min={fromDate || undefined}
                            />
                        </>
                    )}
                </div>

                {/* Summary Cards */}
                <div className={styles.summaryCardsRow}>
                    {summaryCards.map((card) => (
                        <div key={card.label} className={styles.summaryCard}>
                            <div className={`${styles.summaryCardIcon} ${styles[`summaryCardIcon${card.label}`]}`}>
                                <div className={`${styles.summaryCardDot} ${styles[`summaryCardDot${card.label}`]}`} />
                            </div>
                            <div className={styles.summaryCardInfo}>
                                <span className={styles.summaryCardLabel}>{card.displayLabel}</span>
                                <span className={styles.summaryCardValue}>{card.value.toLocaleString()}</span>
                            </div>
                        </div>
                    ))}
                </div>

                <div className={`card flex-fill table-content-show ${!initialLoad && loading ? styles.tableLoading : ""}`}>
                    <div className={styles.filterBarHeader}>
                        <span className={styles.filterBarTitle}>
                            {statusFilter ? `${statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Widgets` : "All Widgets"}
                        </span>
                        <div className={styles.filterPillsWrap}>
                            {["active", "inactive"].map((f) => {
                                const isSelected = statusFilter === f;
                                const pillClass = isSelected
                                    ? (f === "active" ? styles.filterPillGreenActive : styles.filterPillRedActive)
                                    : "";
                                const dotClass = isSelected
                                    ? (f === "active" ? styles.filterDotGreen : styles.filterDotRed)
                                    : styles.filterDotGrey;
                                return (
                                    <button
                                        key={f}
                                        onClick={() => handleStatusFilter(isSelected ? "" : f)}
                                        className={`${styles.filterPill} ${pillClass}`}
                                    >
                                        <span className={`${styles.filterDot} ${dotClass}`} />
                                        {f.charAt(0).toUpperCase() + f.slice(1)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="table-responsive">
                        <DataTable
                            columns={COLUMNS}
                            data={data}
                            progressPending={initialLoad && loading}
                            progressComponent={<Spining />}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[10]}
                            paginationComponentOptions={{ noRowsPerPage: true }}
                            onChangePage={(page) => setCurrentPage(page)}
                            sortServer
                            onSort={handleSort}
                            customStyles={customTableStylesForSummery}
                            highlightOnHover
                            noHeader
                        />
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default LiveWidgetTable;
